/**
 * Live in-app camera for the capture screen. One MediaStream is kept across re-renders (views are re-drawn
 * from template strings, which replaces the <video> element), and re-attached after each render.
 * Falls back to the file picker (which on phones opens the native camera) when getUserMedia is unavailable,
 * blocked, or the page isn't served over HTTPS.
 */
export interface Shot { blob: Blob; w: number; h: number }
export type CamState = 'off' | 'starting' | 'live' | 'denied' | 'unsupported';

export const Camera = {
  stream: null as MediaStream | null,
  state: 'off' as CamState,
  error: '' as string,
  facing: 'environment' as 'environment' | 'user',
  onChange: (() => {}) as () => void,
  /** One <video> for the whole session, moved into each render's slot so the preview never restarts. */
  video: null as HTMLVideoElement | null,
  el() {
    if (!this.video) {
      const v = document.createElement('video');
      v.setAttribute('playsinline', ''); v.muted = true; v.autoplay = true; v.setAttribute('aria-label', 'Camera preview'); v.dataset.camera = '';
      this.video = v;
    }
    return this.video;
  },

  supported() { return !!(navigator.mediaDevices?.getUserMedia) && window.isSecureContext; },

  async start() {
    if (this.stream || this.state === 'starting') return;
    if (!this.supported()) { this.state = 'unsupported'; this.onChange(); return; }
    this.state = 'starting';
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: this.facing }, width: { ideal: 1920 }, height: { ideal: 1440 } },
      });
      this.state = 'live'; this.error = '';
    } catch (e: any) {
      this.stream = null;
      this.state = e?.name === 'NotAllowedError' || e?.name === 'SecurityError' ? 'denied' : 'unsupported';
      this.error = e?.message || String(e);
    }
    this.onChange();
  },

  /** Hooks the live stream to whatever <video data-camera> the current render produced. */
  attach() {
    const slot = document.querySelector('[data-camera-slot]');
    if (!slot) { this.stop(); return; }
    const v = this.el();
    if (v.parentElement !== slot) slot.appendChild(v); // same task as the render, so playback isn't interrupted
    if (!this.stream) { this.start(); return; }
    if (v.srcObject !== this.stream) v.srcObject = this.stream;
    if (v.paused) v.play().catch(() => {});
  },

  stop() {
    if (this.video) { this.video.srcObject = null; this.video.remove(); }
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = null;
    if (this.state === 'live' || this.state === 'starting') this.state = 'off';
  },

  async flip() {
    this.facing = this.facing === 'environment' ? 'user' : 'environment';
    this.stop(); await this.start(); this.attach();
  },

  /**
   * Grabs the current frame straight into the two sizes the app stores (≤1600 px photo, ≤400 px thumbnail).
   * The frame is copied synchronously, so the shot is exactly what was on screen at the tap; encoding follows.
   */
  async snap(): Promise<{ big: Shot; small: Shot }> {
    const v = this.video;
    if (!v || !v.videoWidth) throw new Error('Camera isn’t ready yet');
    const frame = (max: number) => {
      const k = Math.min(1, max / Math.max(v.videoWidth, v.videoHeight));
      const c = document.createElement('canvas'); c.width = Math.round(v.videoWidth * k); c.height = Math.round(v.videoHeight * k);
      c.getContext('2d')!.drawImage(v, 0, 0, c.width, c.height); return c;
    };
    const cb = frame(1600), cs = frame(400);
    const enc = (c: HTMLCanvasElement, q: number) => new Promise<Shot>((res, rej) => c.toBlob(b => b ? res({ blob: b, w: c.width, h: c.height }) : rej(new Error('Could not capture the frame')), 'image/jpeg', q));
    const [big, small] = await Promise.all([enc(cb, 0.82), enc(cs, 0.75)]);
    return { big, small };
  },
};

// Release the camera when the tab is hidden; pick it back up when visible again.
document.addEventListener('visibilitychange', () => {
  if (document.hidden) Camera.stop();
  else if (document.querySelector('[data-camera-slot]')) Camera.attach();
});
